import { useToast } from '@hooks/useToast';
import { isApiError, resendVerification } from '@services/auth.service';
import { useAuthStore } from '@stores/auth.store';
import { Mail, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Non-blocking soft gate: a dismissible amber strip shown across the
 * authenticated workspace while the signed-in user hasn't verified their
 * email. Offers a one-tap resend and a link to the code-entry screen.
 * Dismissal is per-session (component state) — it returns on next load
 * until the account is actually verified.
 */
export function EmailVerificationBanner(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);

  if (!user || user.is_email_verified || dismissed) return null;

  const handleResend = async (): Promise<void> => {
    setResending(true);
    try {
      await resendVerification();
      toast.success({ title: 'Verification code sent', message: 'Check your inbox.' });
    } catch (err) {
      toast.error({
        title: 'Could not resend',
        message: isApiError(err) ? err.message : 'Please try again shortly.',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-8 py-2.5 text-[13px] text-amber-800">
      <Mail size={16} strokeWidth={2} className="shrink-0" />
      <span className="flex-1">
        Verify your email to secure your account.{' '}
        <Link to="/verify-email" className="font-semibold underline">
          Enter your code
        </Link>{' '}
        or
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="ml-1 font-semibold underline disabled:opacity-50"
        >
          {resending ? 'sending…' : 'resend it'}
        </button>
        .
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="shrink-0 rounded p-1 hover:bg-amber-100"
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
}
